// ──────────────────────────────────────────────────────────────────────────
// Formato de fechas del core RU.
// Formato pedido por Ricardo: día (número) - mes (en letra) - año (número).
//   Ej: "2026-06-06" → "6 jun 2026"
//
// IMPORTANTE: este helper es SOLO para MOSTRAR fechas a la persona (tablas,
// tarjetas, detalles). NUNCA usarlo para el `value` de un <input type="date">
// (esos deben seguir en formato ISO yyyy-mm-dd), ni para comparar/ordenar/guardar.
// ──────────────────────────────────────────────────────────────────────────

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

// Descompone un valor de fecha (Date | "yyyy-mm-dd" | ISO | otro) en {y,m,d}
// sin sufrir corrimiento de zona horaria para los strings yyyy-mm-dd (lo más común).
function partes(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    if (isNaN(value)) return null;
    return { y: value.getFullYear(), m: value.getMonth(), d: value.getDate() };
  }
  const s = String(value).trim();
  if (!s) return null;
  // Fecha PURA "yyyy-mm-dd" (SIN hora): se toman los números literales para NO sufrir
  // corrimiento de zona horaria — una fecha de vigencia no debe cambiar de día.
  const soloFecha = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (soloFecha) return { y: +soloFecha[1], m: +soloFecha[2] - 1, d: +soloFecha[3] };
  // Timestamp con hora/zona (ISO, ej. creado_en en UTC): se convierte a hora LOCAL
  // (Panamá UTC−5) y se toma esa fecha. Así el día no salta a UTC de madrugada.
  const dt = new Date(s);
  if (isNaN(dt)) return null;
  return { y: dt.getFullYear(), m: dt.getMonth(), d: dt.getDate() };
}

// Fecha legible: "6 jun 2026". Devuelve `fallback` ('' por defecto) si no hay fecha.
export function fmtFecha(value, fallback = '') {
  const p = partes(value);
  if (!p || p.m < 0 || p.m > 11) return value ? String(value) : fallback;
  return `${p.d} ${MESES[p.m]} ${p.y}`;
}

// Fecha + hora: "6 jun 2026, 14:30". Útil para timestamps (creado_en, etc.).
export function fmtFechaHora(value, fallback = '') {
  if (!value) return fallback;
  const base = fmtFecha(value, '');
  if (!base) return fallback;
  const dt = value instanceof Date ? value : new Date(value);
  if (isNaN(dt)) return base;
  const hh = String(dt.getHours()).padStart(2, '0');
  const mm = String(dt.getMinutes()).padStart(2, '0');
  // Solo agrega la hora si el valor traía componente de hora.
  const tieneHora = value instanceof Date || /\d{2}:\d{2}/.test(String(value));
  return tieneHora ? `${base}, ${hh}:${mm}` : base;
}

// Suma 12 meses (un año) a una fecha ISO yyyy-mm-dd y devuelve ISO yyyy-mm-dd.
// Para auto-completar la fecha de vencimiento a partir de la de inicio.
// Devuelve '' si la entrada no es válida.
export function sumarUnAnioISO(fechaISO) {
  const p = partes(fechaISO);
  if (!p) return '';
  // Construye la fecha al mediodía para evitar corrimientos de zona horaria.
  const dt = new Date(p.y + 1, p.m, p.d, 12, 0, 0);
  if (isNaN(dt)) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
